/**
 * Quiz audience variants — used to swap photos, copy, and demographic
 * personalization based on which community the user picks (or which ad set
 * they came from).
 *
 * Set via the `?a=` URL query param at the start of the quiz. Encoded as a
 * single letter so the URL stays short and doesn't leak what we're showing
 * to a curious user inspecting the bar:
 *   m = men   s = sapphic   g = general
 *
 *   `/app/quiz?stepper=1&a=m`
 *
 * Falls back to "mixed" for organic traffic. Photo paths resolve to
 * `client/public/img/quiz/...` static assets — when an asset doesn't exist
 * yet, the rendering components fall back to gradient placeholders. So new
 * photos can be dropped in one at a time without touching code.
 */

export type QuizAudience = "men" | "sapphic" | "mixed";

export type AgeBucket = "1829" | "3039" | "4049" | "50plus";

const VALID_AUDIENCES: QuizAudience[] = ["men", "sapphic", "mixed"];

const SESSION_AUDIENCE_KEY = "quizAudience";

/** Public URL param name for the audience. */
export const AUDIENCE_PARAM = "a";

const AUDIENCE_TO_PARAM: Record<QuizAudience, string> = {
  men: "m",
  sapphic: "s",
  mixed: "g",
};

const PARAM_TO_AUDIENCE: Record<string, QuizAudience> = {
  m: "men",
  s: "sapphic",
  g: "mixed",
};

export function audienceToParam(audience: QuizAudience): string {
  return AUDIENCE_TO_PARAM[audience];
}

export function audienceFromParam(param: string | null | undefined): QuizAudience | null {
  if (!param) return null;
  return PARAM_TO_AUDIENCE[param] ?? null;
}

/** User-facing community picker — same options as the audience set. */
export type QuizCommunity = "mixed" | "men" | "sapphic";

export function communityToAudience(community: QuizCommunity): QuizAudience {
  return community;
}

/**
 * Was the audience set explicitly via the URL `?a=` param? Used to decide
 * whether the in-app community picker step needs to be shown.
 */
export function hasURLAudience(): boolean {
  if (typeof window === "undefined") return false;
  const param = new URLSearchParams(window.location.search).get(AUDIENCE_PARAM);
  return audienceFromParam(param) !== null;
}

/**
 * Resolve the audience for the current quiz session.
 *
 * Resolution order:
 *   1. `?a=` URL param        (paid-traffic ad URLs)
 *   2. sessionStorage entry   (set by the in-app community picker)
 *   3. `general`              (default for organic traffic)
 *
 * Pure read — sessionStorage writes happen via setQuizAudience.
 */
export function getQuizAudience(): QuizAudience {
  if (typeof window === "undefined") return "mixed";
  const param = new URLSearchParams(window.location.search).get(AUDIENCE_PARAM);
  const fromUrl = audienceFromParam(param);
  if (fromUrl) return fromUrl;
  try {
    const stored = window.sessionStorage.getItem(SESSION_AUDIENCE_KEY);
    if (stored && (VALID_AUDIENCES as string[]).includes(stored)) {
      return stored as QuizAudience;
    }
  } catch {
    // sessionStorage may be unavailable (private mode / SSR) — fall through.
  }
  return "mixed";
}

/** Persist the user's community pick for the rest of the quiz session. */
export function setQuizAudience(audience: QuizAudience): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_AUDIENCE_KEY, audience);
  } catch {
    // ignore — sessionStorage may be unavailable
  }
}

/** Wipe the persisted community pick (used when user opens the picker again). */
export function clearQuizAudienceSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SESSION_AUDIENCE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Short label for the audience used inside the "Not looking for {X}?" banner.
 * Returns null for `general` because the banner is only shown when the user
 * is in a narrowed community and might want to opt out.
 */
export function getAudienceBannerLabel(audience: QuizAudience): string | null {
  if (audience === "sapphic") return "sapphic users";
  if (audience === "men") return "men";
  return null;
}

/**
 * Path to the age-bucket card photo for an audience. Files live at
 * /img/quiz/quiz-{audience}-age-{bucket}.webp (served from public/).
 *
 * If the file doesn't exist on disk, the <img> will fail to load and the
 * component should render its gradient fallback instead.
 */
export function getAgeCardPhoto(audience: QuizAudience, bucket: AgeBucket): string {
  return `/img/quiz/quiz-${audience}-age-${bucket}.webp`;
}

/** Path to the audience-specific location-confirm hero photo. */
export function getLocationHeroPhoto(audience: QuizAudience): string {
  return `/img/quiz/quiz-${audience}-location.webp`;
}

/**
 * Audience-flavored copy now lives in i18n under `quiz.stepper.audience.*`.
 * Components fetch via `t("quiz.stepper.audience.{audience}.{key}")` rather
 * than this module — see AgeRangeIntro for the reference usage. No code-side
 * map is maintained here anymore so translations stay in one place.
 */
